import { useState, useEffect } from 'react';
import { Server } from '@wajeez/wajeez.microservices';
import {
  Control,
  FieldArrayWithId,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import {
  FormInputController,
  FormNumberInputV2,
  FormSwitchInputV2,
  FormTextInputV2,
} from '@wajeez-web/components/form-builder';
import { Autocomplete, Select } from '@wajeez-web/components/mui';
import {
  ButtonHeadless,
  InputLabel,
  LanguagesLayout,
} from '@wajeez-web/components/ui';
import { useTranslation } from 'react-i18next';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { AiFillDelete } from 'react-icons/ai';
import { BiLoaderAlt } from 'react-icons/bi';
import {
  createCollectionComponentTypeMultilingual,
  createContentEntityTypeMultilingual,
  editCollectionComponentTypeMultilingual,
  editContentEntityTypeMultilingual,
  useContentSelector,
} from '@wajeez-web/stores/content';

import { ContentEntityTypeFieldOptionsComponents } from './ContentEntityTypeFieldOptionsComponents';
import { ConfirmationModal } from './ConfirmationModal';
import {
  Languages,
  enumMapper,
  enumMapperValueAsNumber,
} from '@wajeez-web/core/utils';
import {
  useCreateContentEntityTypeMultilingual,
  useEditContentEntityTypeMultilingual,
  useGetContentEntityStructuresLookups,
} from '@wajeez-web/components/helpers';

export const fieldTypes: {
  id: Server.ContentEntityTypeFieldType;
  name: string;
}[] = [
  { id: Server.ContentEntityTypeFieldType.Text, name: 'Text' },
  { id: Server.ContentEntityTypeFieldType.AreaText, name: 'AreaText' },
  { id: Server.ContentEntityTypeFieldType.TineMCE, name: 'TineMCE' },
  { id: Server.ContentEntityTypeFieldType.DateTime, name: 'DateTime' },
  { id: Server.ContentEntityTypeFieldType.Date, name: 'Date' },
  { id: Server.ContentEntityTypeFieldType.DragFile, name: 'DragFile' },
  { id: Server.ContentEntityTypeFieldType.UploadFile, name: 'UploadFile' },
  { id: Server.ContentEntityTypeFieldType.UploadImage, name: 'UploadImage' },
  {
    id: Server.ContentEntityTypeFieldType.CallbackSelect,
    name: 'CallbackSelect',
  },
  { id: Server.ContentEntityTypeFieldType.Select, name: 'Select' },
  { id: Server.ContentEntityTypeFieldType.Number, name: 'Number' },
  { id: Server.ContentEntityTypeFieldType.RangePicker, name: 'RangePicker' },
  { id: Server.ContentEntityTypeFieldType.ColorPicker, name: 'ColorPicker' },
  { id: Server.ContentEntityTypeFieldType.Switch, name: 'Switch' },
];

const contentItemTypes = enumMapperValueAsNumber(Server.ContentItemType);
const lockTypes = enumMapperValueAsNumber(Server.LockType);
const progressUpdatePriorities = enumMapperValueAsNumber(
  Server.ProgressUpdatePriority
);

interface FieldsType extends Server.CreateContentEntityTypeFieldV2Input {
  id: number;
}

export interface FormInput extends Server.CreateContentEntityTypeV2Input {
  id?: number;
  fields: FieldsType[];
}

interface ContentEntityTypeFormBuilderProps {
  type: 'Create' | 'Edit';
  onSubmit: (collectionComponentTypeId: number) => void;
  onCancel: () => void;
  data?: Server.ContentEntityTypeDetailedMultilingualOutput | undefined;
}

export const ContentEntityTypeFormBuilder = ({
  type,
  onSubmit,
  onCancel,
  data,
}: ContentEntityTypeFormBuilderProps) => {
  const [isCancelConfirmationModalOpen, setIsCancelConfirmationModalOpen] =
    useState(false);

  const id = data?.id;
  const createContentEntityTypeMultilingualState =
    useCreateContentEntityTypeMultilingual();
  const editContentEntityTypeMultilingualState =
    useEditContentEntityTypeMultilingual();

  const contentEntityStructures = useGetContentEntityStructuresLookups();

  const submitStatus =
    createContentEntityTypeMultilingualState.status === 'loading' ||
    editContentEntityTypeMultilingualState.status === 'loading';

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty, dirtyFields },
  } = useForm<FormInput>({
    defaultValues: data || { profiles: [{ languageId: Languages.AR }] },
  });

  // watch((data) => {
  //   console.log(data);
  // });

  const {
    fields: profilesMain,
    append: appendProfileMain,
    remove: removeProfileMain,
  } = useFieldArray({
    control,
    name: 'profiles',
  });

  const {
    fields,
    prepend: prependField,
    remove: removeField,
  } = useFieldArray({
    control,
    name: 'fields',
  });

  const { t } = useTranslation();

  const onFormSubmit: SubmitHandler<FormInput> = async (data) => {
    const mappedData: FormInput = {
      ...data,
      //   usageType: Number(data.usageType),
    };

    if (type === 'Create') {
      createContentEntityTypeMultilingual(mappedData, onSubmit);
    } else {
      editContentEntityTypeMultilingual({ ...mappedData, id }, onSubmit);
    }
  };

  const renderFields = () => {
    return fields.map((f, i) => {
      return (
        <div key={f.id}>
          <ContentEntityTypeField
            control={control}
            fieldIndex={i}
            field={f}
            onFieldRemove={() => {
              removeField(i);
            }}
            formType={type}
          />
        </div>
      );
    });
  };

  const renderProfileMain = (language: Languages) => {
    const selectedProfileIndexMain = profilesMain.findIndex(
      (p) => p.languageId === language
    );

    return (
      <div
        key={`profiles.${selectedProfileIndexMain}`}
        className="w-[400px] flex flex-col space-y-2"
      >
        <FormTextInputV2
          control={control}
          name={`profiles.${selectedProfileIndexMain}.title`}
          fieldMeta={{
            fieldType: Server.FieldType.Text,
            required: true,
            label: 'Title',
            placeHolder: 'Title',
            fieldOptions: {},
          }}
        />
        <FormTextInputV2
          control={control}
          name={`profiles.${selectedProfileIndexMain}.description`}
          fieldMeta={{
            fieldType: Server.FieldType.Text,
            required: true,
            label: 'Description',
            placeHolder: 'Description',
            fieldOptions: {},
          }}
        />
        <FormTextInputV2
          control={control}
          name={`profiles.${selectedProfileIndexMain}.displayNamePlural`}
          fieldMeta={{
            fieldType: Server.FieldType.Text,
            label: 'Display Name Plural',
            placeHolder: 'Display Name Plural',
            fieldOptions: {},
          }}
        />
        <FormTextInputV2
          control={control}
          name={`profiles.${selectedProfileIndexMain}.displayNameSingular`}
          fieldMeta={{
            fieldType: Server.FieldType.Text,
            label: 'Display Name Singular',
            placeHolder: 'Display Name Singular',
            fieldOptions: {},
          }}
        />
        <FormTextInputV2
          control={control}
          name={`profiles.${selectedProfileIndexMain}.unitNamePlural`}
          fieldMeta={{
            fieldType: Server.FieldType.Text,
            label: 'Unit Name Plural',
            placeHolder: 'Unit Name Plural',
            fieldOptions: {},
          }}
        />
        <FormTextInputV2
          control={control}
          name={`profiles.${selectedProfileIndexMain}.unitNameSingular`}
          fieldMeta={{
            fieldType: Server.FieldType.Text,
            label: 'Unit Name Singular',
            placeHolder: 'Unit Name Singular',
            fieldOptions: {},
          }}
        />
        <FormTextInputV2
          control={control}
          name={`profiles.${selectedProfileIndexMain}.colorCode`}
          fieldMeta={{
            fieldType: Server.FieldType.Text,
            label: 'Color Code',
            placeHolder: 'Color Code',
            fieldOptions: {},
          }}
        />
        <FormNumberInputV2
          control={control}
          name={`profiles.${selectedProfileIndexMain}.order`}
          fieldMeta={{
            fieldType: Server.FieldType.Number,
            label: 'Order',
            placeHolder: 'Order',
            fieldOptions: {},
          }}
        />
      </div>
    );
  };

  return (
    <div className="w-full">
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="w-full flex flex-col justify-start items-start"
        noValidate
      >
        <div className="w-full pb-6 border-b-[2px]">
          <div className="w-full">
            <FormSectionTitle sectionNumber={1} title="Basic Information" />
            <div className="w-[400px] flex flex-col justify-start items-start space-y-2">
              <FormTextInputV2
                control={control}
                name="name"
                fieldMeta={{
                  fieldType: Server.FieldType.Text,
                  required: true,
                  label: 'Name',
                  placeHolder: 'Name',
                  fieldOptions: {},
                }}
                disabled={type === 'Edit'}
              />
              <FormInputController
                control={control}
                name={`contentEntityStructureId`}
                fieldMeta={{
                  fieldType: Server.FieldType.Select,
                  fieldOptions: {},
                  required: true,
                }}
                renderInput={({ ...props }) => (
                  <div className="w-full">
                    <Autocomplete
                      id={`contentEntityStructureId-autocomplete`}
                      options={contentEntityStructures.data.map((cs) => {
                        return {
                          value: cs.id,
                          label: cs.name,
                        };
                      })}
                      label="Content Entity Structure"
                      color="#1f7983"
                      heightClassName="h-full"
                      required
                      {...props}
                    />
                  </div>
                )}
              />
              <FormNumberInputV2
                control={control}
                name={`pointsValue`}
                fieldMeta={{
                  fieldType: Server.FieldType.Number,
                  label: 'Points Value',
                  placeHolder: 'Points Value',
                  fieldOptions: {},
                }}
              />
              <FormNumberInputV2
                control={control}
                name={`weight`}
                fieldMeta={{
                  fieldType: Server.FieldType.Number,
                  label: 'Weight',
                  placeHolder: 'Weight',
                  fieldOptions: {},
                }}
              />
              <FormInputController
                control={control}
                name={`contentItemType`}
                fieldMeta={{
                  fieldType: Server.FieldType.Select,
                  fieldOptions: {},
                }}
                renderInput={({ ...props }) => (
                  <div className="w-full">
                    <Autocomplete
                      id={`contentItemType-autocomplete`}
                      options={contentItemTypes}
                      label="Content Item Type"
                      color="#1f7983"
                      heightClassName="h-full"
                      required
                      {...props}
                    />
                  </div>
                )}
              />
              <FormInputController
                control={control}
                name={`lockType`}
                fieldMeta={{
                  fieldType: Server.FieldType.Select,
                  fieldOptions: {},
                }}
                renderInput={({ ...props }) => (
                  <div className="w-full">
                    <Autocomplete
                      id={`lockType-autocomplete`}
                      options={lockTypes}
                      label="Lock Type"
                      color="#1f7983"
                      heightClassName="h-full"
                      required
                      {...props}
                    />
                  </div>
                )}
              />
              <FormInputController
                control={control}
                name={`progressUpdatePriority`}
                fieldMeta={{
                  fieldType: Server.FieldType.Select,
                  fieldOptions: {},
                }}
                renderInput={({ ...props }) => (
                  <div className="w-full">
                    <Autocomplete
                      id={`progressUpdatePriority-autocomplete`}
                      options={progressUpdatePriorities}
                      label="Progress Update Priority"
                      color="#1f7983"
                      heightClassName="h-full"
                      required
                      {...props}
                    />
                  </div>
                )}
              />
            </div>
            <div className="mt-5 w-[700px] grid grid-cols-2 gap-y-2">
              <FormSwitchInputV2
                control={control}
                name={`requireAudio`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Require Audio',
                  placeHolder: 'Require Audio',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`narratorSelectable`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Narrator Selectable',
                  placeHolder: 'Narrator Selectable',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`requireText`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Require Text',
                  placeHolder: 'Require Text',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`requirePublishDate`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Require Publish Date',
                  placeHolder: 'Require Publish Date',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`requireAuthor`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Require Author',
                  placeHolder: 'Require Author',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`requireActualCost`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Require Actual Cost',
                  placeHolder: 'Require Actual Cost',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`requireActualDurationMS`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Require Actual Duration MS',
                  placeHolder: 'Require Actual Duration MS',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`requireOriginalTitle`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Require Original Title',
                  placeHolder: 'Require Original Title',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`requireOriginalSubTitle`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Require Original Subtitle',
                  placeHolder: 'Require Original Subtitle',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`isQuizable`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Is Quizable',
                  placeHolder: 'Is Quizable',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`shownInSideBar`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Shown In Sidebar',
                  placeHolder: 'Shown In Sidebar',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`showInHome`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Show In Home',
                  placeHolder: 'Show In Home',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`hasCertificate`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Has Certificate',
                  placeHolder: 'Has Certificate',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`containsQuotes`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Contains Quotes',
                  placeHolder: 'Contains Quotes',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`allowViewsCalculation`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Allow Views Calculation',
                  placeHolder: 'Allow Views Calculation',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`allowConcatenation`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Allow Concatenation',
                  placeHolder: 'Allow Concatenation',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`allowIntegrationSyncMetaData`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Allow Integration Sync Meta Data',
                  placeHolder: 'Allow Integration Sync Meta Data',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`canBeAssignedToWhenPublished`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Can Be Assigned To When Published',
                  placeHolder: 'Can Be Assigned To When Published',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`canBeAssignedWhenPublished`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Can Be Assigned When Published',
                  placeHolder: 'Can Be Assigned When Published',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`allowProgressReset`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Allow Progress Reset',
                  placeHolder: 'Allow Progress Reset',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`isFreeDailyContent`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Is Free Daily Content',
                  placeHolder: 'Is Free Daily Content',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`includedInDurationCalculation`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Included In Duration Calculation',
                  placeHolder: 'Included In Duration Calculation',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`includedInCountCalculation`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Included In Count Calculation',
                  placeHolder: 'Included In Count Calculation',
                  fieldOptions: {},
                }}
              />
              <FormSwitchInputV2
                control={control}
                name={`isRewardable`}
                fieldMeta={{
                  fieldType: Server.FieldType.Switch,
                  label: 'Is Rewardable',
                  placeHolder: 'Is Rewardable',
                  fieldOptions: {},
                }}
              />
            </div>
            <div className="mt-5">
              <LanguagesLayout
                renderPage={renderProfileMain}
                defaultLanguages={profilesMain.map((s) => s.languageId)}
                onAdd={function (language: Languages): void {
                  appendProfileMain({
                    languageId: language,
                    title: '',
                    description: '',
                  });
                }}
                onDelete={function (language: Languages): void {
                  const selectedProfileIndexMain = profilesMain.findIndex(
                    (p) => p.languageId === language
                  );

                  removeProfileMain(selectedProfileIndexMain);
                }}
              />
            </div>
          </div>
        </div>
        <div className="mt-6">
          <FormSectionTitle sectionNumber={2} title="Dynamic Fields" />
          <div className="w-[150px]">
            <InputLabel
              text={
                t(`inputs.labels.${'Add Field'}`, 'Add Field' || '') as string
              }
              name={'fieldType' as string}
            />
            <div className="h-[30px]">
              <Select
                name="fieldType"
                options={fieldTypes.map((f) => ({
                  value: f.id.toString(),
                  label: f.name,
                }))}
                onChange={(option) => {
                  const value = Number(
                    option.value
                  ) as Server.ContentEntityTypeFieldType;

                  prependField({
                    id: Date.now(),
                    fieldType: value,
                    name: '',
                    fieldOptions: {},
                    required: false,
                    profiles: [
                      {
                        languageId: Languages.AR,
                        label: '',
                        placeHolder: '',
                        span: '',
                      },
                    ],
                  });
                }}
                option={{ value: '', label: '' }}
                disabled={type === 'Edit'}
              />
            </div>
          </div>
          <div className="mt-3 w-full">{renderFields()}</div>
        </div>
        <div className="absolute top-[-40px] rtl:left-0 ltr:right-0 w-full flex justify-end items-center space-s-2">
          <div className="mt-10 w-[70px]">
            <ButtonHeadless
              bgStyle="bg-warmGray-300"
              paddingYStyle="py-1"
              borderRadiusStyle="rounded-[6px]"
              action={() => {
                if (isDirty) {
                  setIsCancelConfirmationModalOpen(true);
                } else {
                  onCancel && onCancel();
                }
              }}
            >
              <div className="text-warmGray-400 text-[16px] font-bold">
                <ReturnTranslatedText
                  origintext="Cancel"
                  text={`buttons.${'Cancel'}`}
                />
              </div>
            </ButtonHeadless>
          </div>
          <div className="mt-10 w-[70px]">
            <ButtonHeadless
              type="submit"
              paddingYStyle="py-1"
              borderRadiusStyle="rounded-[6px]"
              isDisabled={submitStatus}
            >
              <div className="flex justify-center items-center text-white text-[16px] font-bold">
                <ReturnTranslatedText
                  origintext="Save"
                  text={`buttons.${'Save'}`}
                />
                {submitStatus && (
                  <BiLoaderAlt className="mr-1 animate-spin w-5" />
                )}
              </div>
            </ButtonHeadless>
          </div>
        </div>
      </form>
      {isCancelConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isCancelConfirmationModalOpen}
          onClose={() => {
            setIsCancelConfirmationModalOpen(false);
          }}
          onSubmit={() => {
            onCancel && onCancel();
          }}
          message="You didn't save the changes, are you sure that you want to cancel?"
        />
      )}
    </div>
  );
};

interface ContentEntityTypeFieldProps {
  control: Control<FormInput, any>;
  fieldIndex: number;
  field: FieldArrayWithId<FormInput, 'fields', 'id'>;
  onFieldRemove: () => void;
  formType: 'Create' | 'Edit';
}

const ContentEntityTypeField = ({
  control,
  fieldIndex,
  field,
  onFieldRemove,
  formType,
}: ContentEntityTypeFieldProps) => {
  const {
    fields: fieldProfiles,
    append: appendFieldProfile,
    remove: removeFieldProfile,
  } = useFieldArray({
    control,
    name: `fields.${fieldIndex}.profiles`,
  });

  const renderFieldProfile = (language: Languages) => {
    const selectedFieldProfileIndex = fieldProfiles.findIndex(
      (p) => p.languageId === language
    );

    return (
      <div
        key={`fields.${fieldIndex}.profiles.${selectedFieldProfileIndex}`}
        className="w-full flex flex-col space-y-2"
      >
        <FormTextInputV2
          control={control}
          name={`fields.${fieldIndex}.profiles.${selectedFieldProfileIndex}.label`}
          fieldMeta={{
            fieldType: Server.FieldType.Text,
            required: true,
            label: 'Label',
            placeHolder: 'Label',
            fieldOptions: {},
          }}
        />
        <FormTextInputV2
          control={control}
          name={`fields.${fieldIndex}.profiles.${selectedFieldProfileIndex}.placeHolder`}
          fieldMeta={{
            fieldType: Server.FieldType.Text,
            required: true,
            label: 'Placeholder',
            placeHolder: 'Placeholder',
            fieldOptions: {},
          }}
        />
      </div>
    );
  };

  return (
    <div className="mt-3 w-[650px] p-4 flex justify-between items-center border rounded">
      <div className="w-full">
        <div className="mb-3 text-[17px] font-bold">
          {fieldTypes.find((t) => t.id === field.fieldType)?.name}
        </div>
        <div className="w-full grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <FormSwitchInputV2
              control={control}
              name={`fields.${fieldIndex}.required`}
              fieldMeta={{
                fieldType: Server.FieldType.Switch,
                label: 'Required?',
                fieldOptions: {},
              }}
            />
          </div>
          <FormTextInputV2
            control={control}
            name={`fields.${fieldIndex}.name`}
            fieldMeta={{
              fieldType: Server.FieldType.Text,
              required: true,
              label: 'Name',
              placeHolder: 'Name',
              fieldOptions: {},
            }}
            disabled={formType === 'Edit'}
          />
          <div className="col-span-2">
            <LanguagesLayout
              renderPage={renderFieldProfile}
              defaultLanguages={fieldProfiles.map((s) => s.languageId)}
              onAdd={function (language: Languages): void {
                appendFieldProfile({
                  languageId: language,
                  label: '',
                  placeHolder: '',
                  span: '',
                });
              }}
              onDelete={function (language: Languages): void {
                const selectedProfileIndexMain = fieldProfiles.findIndex(
                  (p) => p.languageId === language
                );

                removeFieldProfile(selectedProfileIndexMain);
              }}
            />
          </div>
          <ContentEntityTypeFieldOptionsComponents
            fieldType={field.fieldType}
            fieldIndex={fieldIndex}
            name={`fields.${fieldIndex}.fieldOptions`}
            control={control}
          />
        </div>
      </div>
      {formType !== 'Edit' && (
        <div
          className="mr-[30px] text-red-500 cursor-pointer hover:opacity-75 active:opacity-100"
          onClick={onFieldRemove}
        >
          <AiFillDelete />
        </div>
      )}
    </div>
  );
};

interface FormSectionTitleProps {
  sectionNumber: number;
  title: string;
}

const FormSectionTitle = ({ sectionNumber, title }: FormSectionTitleProps) => {
  return (
    <div className="mb-4 w-full flex justify-start items-center">
      <div className="w-5 h-5 flex justify-center items-center bg-[#212529] rounded-full text-white text-[15px] font-medium">
        {sectionNumber}
      </div>
      <div className="rtl:mr-2 ltr:ml-2 text-[19px] font-medium">
        <ReturnTranslatedText text={`general.${title}`} origintext={title} />
      </div>
    </div>
  );
};
